import React, { useEffect, useRef } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js"
import { Line, Pie, Bar, getElementAtEvent } from "react-chartjs-2"
import { cn } from "@/lib/utils"

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend
)

interface BaseChartProps {
  width?: number | string
  height?: number | string
  className?: string
}

interface LineChartProps extends BaseChartProps {
  data: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      borderColor?: string
      backgroundColor?: string
      tension?: number
    }[]
  }
  options?: Partial<ChartOptions<"line">>
}

interface PieChartProps extends BaseChartProps {
  data: {
    labels: string[]
    datasets: {
      data: number[]
      backgroundColor: string[]
      borderWidth?: number
    }[]
  }
  options?: Partial<ChartOptions<"pie">>
}

interface BarChartProps extends BaseChartProps {
  data: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      backgroundColor?: string | string[]
      borderColor?: string | string[]
    }[]
  }
  options?: Partial<ChartOptions<"bar">>
}

// Common chart styling for fintech
const getCommonOptions = (darkMode = true): Partial<ChartOptions<any>> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top" as const,
      labels: {
        color: darkMode ? "#94A3B8" : "#475569",
        usePointStyle: true,
        font: {
          size: 12,
          family: "Inter, system-ui, sans-serif",
        },
      },
    },
    tooltip: {
      backgroundColor: darkMode ? "#0F172A" : "#FFFFFF",
      titleColor: darkMode ? "#F8FAFC" : "#0F172A",
      bodyColor: darkMode ? "#CBD5E1" : "#475569",
      borderColor: darkMode ? "#334155" : "#E2E8F0",
      borderWidth: 1,
      cornerRadius: 8,
      font: {
        family: "Inter, system-ui, sans-serif",
      },
    },
  },
  scales: {
    x: {
      grid: {
        color: darkMode ? "#1E293B" : "#F1F5F9",
      },
      ticks: {
        color: darkMode ? "#94A3B8" : "#475569",
        font: {
          size: 11,
          family: "Inter, system-ui, sans-serif",
        },
      },
    },
    y: {
      grid: {
        color: darkMode ? "#1E293B" : "#F1F5F9",
      },
      ticks: {
        color: darkMode ? "#94A3B8" : "#475569",
        font: {
          size: 11,
          family: "Inter, system-ui, sans-serif",
        },
      },
    },
  },
})

export function SimpleLineChart({ data, options, className, height = 300 }: LineChartProps) {
  const chartOptions = {
    ...getCommonOptions(),
    ...options,
  }

  return (
    <div className={cn("relative", className)} style={{ height }}>
      <Line data={data} options={chartOptions} />
    </div>
  )
}

export function SimplePieChart({ data, options, className, height = 300 }: PieChartProps) {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          color: "#94A3B8",
          usePointStyle: true,
          font: {
            size: 12,
            family: "Inter, system-ui, sans-serif",
          },
        },
      },
      tooltip: {
        backgroundColor: "#0F172A",
        titleColor: "#F8FAFC",
        bodyColor: "#CBD5E1",
        borderColor: "#334155",
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            const label = context.label || ""
            const value = context.parsed || 0
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            return `${label}: ${percentage}%`
          },
        },
      },
    },
    ...options,
  }

  return (
    <div className={cn("relative", className)} style={{ height }}>
      <Pie data={data} options={chartOptions} />
    </div>
  )
}

export function SimpleBarChart({ data, options, className, height = 300 }: BarChartProps) {
  const chartOptions = {
    ...getCommonOptions(),
    ...options,
  }

  return (
    <div className={cn("relative", className)} style={{ height }}>
      <Bar data={data} options={chartOptions} />
    </div>
  )
}

// Performance chart specifically for financial data
interface PerformanceChartProps extends BaseChartProps {
  data: {
    timestamps: string[]
    values: number[]
    label?: string
  }
  showGrid?: boolean
  color?: string
}

export function PerformanceChart({ 
  data, 
  className, 
  height = 200, 
  showGrid = true,
  color = "#00D09C" 
}: PerformanceChartProps) {
  const chartData = {
    labels: data.timestamps,
    datasets: [
      {
        label: data.label || "Performance",
        data: data.values,
        borderColor: color,
        backgroundColor: `${color}20`,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  }

  const options = {
    ...getCommonOptions(),
    scales: {
      x: {
        display: showGrid,
        grid: {
          display: showGrid,
          color: "#1E293B",
        },
        ticks: {
          display: showGrid,
          maxTicksLimit: 6,
        },
      },
      y: {
        display: showGrid,
        grid: {
          display: showGrid,
          color: "#1E293B",
        },
        ticks: {
          display: showGrid,
          callback: (value: any) => {
            return `$${value.toLocaleString()}`
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `${data.label || "Value"}: $${context.parsed.y.toLocaleString()}`
          },
        },
      },
    },
  }

  return (
    <div className={cn("relative", className)} style={{ height }}>
      <Line data={chartData} options={options} />
    </div>
  )
}